import { channel } from 'redux-saga';

import { all, select, take, put, takeLatest } from 'redux-saga/effects';

import { some } from 'lodash';

import fetchData from '../../api/fetchData';
import WebSocketConnector from '../../api/sockets/WebSocketConnector';
import { SOCKET_STATUS_CHANNEL } from '../../consts/socketChannels';

import { shouldUseLocalStorageCRMStatus } from '../serializers/currentManager';

import { serializeManagerStatuses } from '../serializers/status';
import {
  SET_LEADER_INSTANCE,
  WS_UPDATE_CRM_STATUS,
  CHANGE_CRM_MANUAL_STATE,
  SET_CRM_STATUS,
  SET_CRM_STATUS_SUCCESS,
  SET_MANAGER_STATUSES,
  REFETCH_MANAGER_STATUSES,
} from '../actions/general';

import { SAVE_CRITICAL_ERRORS } from '../../consts/errors';

import StatusChannelHandler from './socketHandlers/StatusChannelHandler';
import {
  CRM_STATES,
  CRM_LOCALSTORAGE_LAST_STATE,
  CRM_LOCALSTORAGE_LAST_STATE_CHANGED_AT,
} from '../../consts/general';

import { safeApply, safeCall } from './errors';

function* wsUpdateOnlineStatus({ payload }) {
  const { isMasterInstance } = yield select(state => state.general);

  if (!isMasterInstance) return;

  const { operatorState: remoteOperatorState } = payload;

  yield put({
    payload: {
      operatorState: remoteOperatorState,
    },
    type: SET_CRM_STATUS_SUCCESS,
  });
}

function* setStatus({ payload }) {
  const { currentManager } = yield select(state => state.general);
  const { isManual, isRetry, operatorState } = payload;

  if (isManual) {
    yield localStorage.setItem(CRM_LOCALSTORAGE_LAST_STATE, operatorState);
    yield localStorage.setItem(
      CRM_LOCALSTORAGE_LAST_STATE_CHANGED_AT,
      Date.now()
    );
  }

  if (currentManager?.remoteState == operatorState) return;

  const data = yield safeCall(
    fetchData,
    `${process.env.API_URL}/managers/${currentManager.id}`,
    {
      method: 'PUT',
      body: JSON.stringify({
        status_id: Number(operatorState),
      }),
      headers: { 'Content-Type': 'application/json' },
    }
  );

  yield put({
    payload: {
      isManual,
      operatorState: Number(data?.status?.id || CRM_STATES.OFFLINE),
    },
    type: SET_CRM_STATUS_SUCCESS,
  });

  if (isRetry || !!data?.meta?.success) return;

  const errors = data?.errors?.base || [];

  yield all([
    put({
      payload: {
        isManual,
        isRetry: true,
        operatorState: CRM_STATES.OFFLINE,
      },
      type: SET_CRM_STATUS,
    }),
    !!errors.length &&
      put({
        error: errors[0],
        type: SAVE_CRITICAL_ERRORS,
      }),
  ]);
}

function* setManualState({ payload }) {
  const { isManual, state } = payload;

  yield put({
    payload: {
      isManual,
      operatorState: state,
    },
    type: SET_CRM_STATUS,
  });
}

function* checkLocalStorageStatus() {
  if (!shouldUseLocalStorageCRMStatus) return;

  const lastOperatorState = localStorage.getItem(CRM_LOCALSTORAGE_LAST_STATE);
  const { managerStatuses } = yield select(state => state.general);
  const isValidStatus = some(
    managerStatuses,
    status => String(status.value) === String(lastOperatorState)
  );

  yield put({
    payload: {
      isManual: true,
      operatorState: isValidStatus
        ? String(lastOperatorState)
        : CRM_STATES.OFFLINE,
    },
    type: SET_CRM_STATUS,
  });
}

function* fetchManagerStatuses() {
  const data = yield safeCall(
    fetchData,
    `${process.env.API_HOST_URL}${process.env.API_MANAGER_STATUSES}`
  );

  if (!data || !data.manager_statuses) return;

  const managerStatuses = serializeManagerStatuses(data.manager_statuses);

  yield put({
    payload: { managerStatuses },
    type: SET_MANAGER_STATUSES,
  });

  yield safeCall(checkLocalStorageStatus);
}

function* watchSocketStatusMessage(webSocketConnector, identifier) {
  const socketChannel = channel();
  const socketHandler = new StatusChannelHandler(socketChannel);
  webSocketConnector.subscribeSocketOnMessage({
    identifier,
    socketHandler,
  });

  while (true) {
    const action = yield take(socketChannel);
    yield put(action);
  }
}

function* subscribeSocketStatus() {
  const { currentManager } = yield select(state => state.general);

  const webSocketConnector = yield safeCall(WebSocketConnector.build);

  if (!webSocketConnector) return;

  const identifier = {
    channel: SOCKET_STATUS_CHANNEL,
    manager_id: currentManager.id,
  };

  webSocketConnector.subscribeChannel(identifier);

  yield safeCall(watchSocketStatusMessage, webSocketConnector, identifier);
}

function* setLeaderInstance() {
  const { isMasterInstance } = yield select(state => state.general);

  if (!isMasterInstance) return;

  yield all([
    safeCall(fetchManagerStatuses),
    safeCall(subscribeSocketStatus),
    takeLatest(WS_UPDATE_CRM_STATUS, safeApply(wsUpdateOnlineStatus)),
    takeLatest(SET_CRM_STATUS, safeApply(setStatus)),
    takeLatest(REFETCH_MANAGER_STATUSES, safeApply(fetchManagerStatuses)),
  ]);
}

export default function* () {
  yield all([
    takeLatest(SET_LEADER_INSTANCE, safeApply(setLeaderInstance)),
    takeLatest(CHANGE_CRM_MANUAL_STATE, safeApply(setManualState)),
  ]);
}
